import { useMemo, useState } from 'react';

import {
  type Parent,
  type UnitRelationLookupOptions,
} from './RelationDivisionLine/RelationDivisionLine.types';
import { type ShareValues } from '@/containers/Finance/Invoices/hoa/shared/types/formTypes';

import relationDivisionLinesCtx from './RelationDivisionLines.context';
import { trpc } from '@/config/trpc';

import {
  createExcludedRelationKeysLookup,
  createUnitOptionLookup,
  getDivisionRelationKeys,
} from './RelationDivisionLine/RelationDivisionLine.utils';

export type RelationDivisionLinesProviderProps = React.PropsWithChildren<{
  divisions: ShareValues[];
  parent: Parent;
}>;

const RelationDivisionLinesProvider: React.FC<RelationDivisionLinesProviderProps> = ({
  children,
  divisions,
  parent,
}) => {
  const [showOldRelations, setShowOldRelations] = useState(false);

  const relationOptionsQueryEnabled = !!parent.bookYearId && !!parent.homeownerAssociationId;

  const relationOptionsQuery = trpc.hoa.building.relation.unitRelationsAutocomplete.useQuery(
    {
      bookYearId: parent.bookYearId ?? '',
      homeownerAssociationId: parent.homeownerAssociationId ?? '',
      options: {
        includeOldRelations: showOldRelations,
      },
    },
    {
      enabled: relationOptionsQueryEnabled,
    },
  );

  const parentValue = useMemo(
    () => ({
      bookYearId: parent.bookYearId,
      divisionTypeName: parent.divisionTypeName,
      homeownerAssociationId: parent.homeownerAssociationId,
      name: parent.name,
      readOnly: parent.readOnly,
    }),
    [
      parent.bookYearId,
      parent.divisionTypeName,
      parent.homeownerAssociationId,
      parent.name,
      parent.readOnly,
    ],
  );

  const relationAutocompleteParamsValue = useMemo(
    () => ({
      setShowOldRelations,
      showOldRelations,
    }),
    [showOldRelations],
  );

  // joined to make dependency equality more lenient
  const excludedRelationsDependencyKey = getDivisionRelationKeys(divisions).join(',');

  const lookups = {
    excludedRelations: useMemo(() => {
      const keys = excludedRelationsDependencyKey.length
        ? excludedRelationsDependencyKey.split(',')
        : [];

      return createExcludedRelationKeysLookup({
        keys,
      });
    }, [excludedRelationsDependencyKey]),
    unitOptions: useMemo(
      () => createUnitOptionLookup(relationOptionsQuery.data ?? []),
      [relationOptionsQuery.data],
    ),
  };

  const relationAutocompleteQueryValue = useMemo(() => {
    const data = relationOptionsQuery.data ?? [];

    const getAvailableOptions = (currentDivision?: UnitRelationLookupOptions) =>
      data.filter(
        (option) => !lookups.excludedRelations.has(option.value.key, { except: currentDivision }),
      );

    return {
      data,
      isLoading: relationOptionsQueryEnabled && relationOptionsQuery.isLoading,
      selectors: {
        getAvailableOptions,
        getOption: lookups.unitOptions.get,
      },
    };
  }, [
    lookups.excludedRelations,
    lookups.unitOptions,
    relationOptionsQuery.data,
    relationOptionsQuery.isLoading,
    relationOptionsQueryEnabled,
  ]);

  return (
    <relationDivisionLinesCtx.parent.Provider value={parentValue}>
      <relationDivisionLinesCtx.relationAutocomplete.params.Provider
        value={relationAutocompleteParamsValue}
      >
        <relationDivisionLinesCtx.relationAutocomplete.query.Provider
          value={relationAutocompleteQueryValue}
        >
          {children}
        </relationDivisionLinesCtx.relationAutocomplete.query.Provider>
      </relationDivisionLinesCtx.relationAutocomplete.params.Provider>
    </relationDivisionLinesCtx.parent.Provider>
  );
};

export default RelationDivisionLinesProvider;
